<template>
  <Layout>
    <div class="journey-builder">
      <!-- Loading overlay (padrão form-builder): segura a interação até a
           jornada E os dados dos dropdowns (triggers, templates, status...)
           estarem prontos — evita clicar num editor ainda vazio -->
      <transition name="fade-overlay">
        <div v-if="booting" class="builder-loading-overlay">
          <div class="builder-loading-content">
            <div class="builder-loading-spinner"></div>
            <p>Carregando jornada...</p>
          </div>
        </div>
      </transition>

      <!-- Header -->
      <div class="builder-header">
        <div class="header-left">
          <button class="btn-back" @click="goBack">
            <i class="bx bx-arrow-back"></i>
          </button>
          <div class="journey-title-section">
            <input
              v-if="journey"
              v-model="journey.name"
              type="text"
              class="journey-title-input"
              placeholder="Nome da jornada"
              @input="markDirty"
            />
            <span v-if="journey" class="journey-status" :class="journey.status">
              {{ statusLabel(journey.status) }}
            </span>
          </div>
        </div>
        <div class="header-right">
          <b-dropdown variant="link" toggle-class="p-0" right no-caret v-if="journey">
            <template v-slot:button-content>
              <button class="btn-status-toggle">
                <i class="bx bx-power-off"></i>
                {{ journey.status === 'active' ? 'Ativa' : 'Ativar' }}
                <i class="bx bx-chevron-down"></i>
              </button>
            </template>
            <b-dropdown-item @click="changeJourneyStatus('active')" v-if="journey.status !== 'active'">
              <i class="bx bx-play text-success mr-1"></i> Ativar
            </b-dropdown-item>
            <b-dropdown-item @click="changeJourneyStatus('paused')" v-if="journey.status === 'active'">
              <i class="bx bx-pause text-warning mr-1"></i> Pausar
            </b-dropdown-item>
            <b-dropdown-item @click="changeJourneyStatus('draft')" v-if="journey.status !== 'draft'">
              <i class="bx bx-pencil text-info mr-1"></i> Rascunho
            </b-dropdown-item>
            <b-dropdown-item @click="changeJourneyStatus('archived')">
              <i class="bx bx-archive text-muted mr-1"></i> Arquivar
            </b-dropdown-item>
          </b-dropdown>
          <button class="btn-save" @click="saveJourney" :disabled="saving || !dirty">
            <i class="bx" :class="saving ? 'bx-loader-alt bx-spin' : 'bx-save'"></i>
            {{ saving ? 'Salvando...' : 'Salvar' }}
          </button>
        </div>
      </div>

      <!-- Main Content (3 panels) -->
      <div class="builder-content">
        <!-- Left Panel: Node Palette -->
        <div class="panel panel-left">
          <NodePalette @add-node="addNode" />
        </div>

        <!-- Center Panel: Journey Canvas -->
        <div class="panel panel-center">
          <div class="canvas-scroll">
            <JourneyCanvas
              :nodes="mainFlowNodes"
              :all-nodes="nodes"
              :selected-node-id="selectedNodeId"
              :email-templates="emailTemplates"
              :wa-templates="waTemplates"
              :statuses="statuses"
              :groups="groups"
              :tags="tags"
              :forms="forms"
              :journeys-list="journeysList"
              :follow-up-rules="followUpRules"
              @select-node="selectNode"
              @reorder-nodes="reorderNodes"
              @add-node-at="addNodeAt"
              @remove-node="removeNode"
            />
          </div>
        </div>

        <!-- Right Panel: Node Editor -->
        <div class="panel panel-right" :class="{ 'has-selection': selectedNode }">
          <div v-if="!selectedNode" class="no-selection">
            <div class="no-selection-icon">
              <i class="bx bx-pointer"></i>
            </div>
            <h5>Nenhum no selecionado</h5>
            <p>Clique em um no para editar suas propriedades</p>
          </div>
          <NodeEditor
            v-else
            :node="selectedNode"
            :email-templates="emailTemplates"
            :wa-templates="waTemplates"
            :statuses="statuses"
            :groups="groups"
            :tags="tags"
            :forms="forms"
            :nodes="nodes"
            :journeys-list="journeysList"
            :field-types="fieldTypes"
            :ref-statuses="refStatuses"
            :form-condition-fields="formConditionFields"
            :follow-up-rules="followUpRules"
            @update-node="updateNode"
            @remove-node="removeNode"
            @close="selectNode(null)"
          />
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/layout/vertical'
import { mapActions, mapState, mapGetters } from 'vuex'
import { successMessage, errorMessage } from '@/components/alertsMessages'
import NodePalette from './components/NodePalette.vue'
import JourneyCanvas from './components/JourneyCanvas.vue'
import NodeEditor from './components/NodeEditor.vue'

export default {
  components: {
    Layout,
    NodePalette,
    JourneyCanvas,
    NodeEditor
  },
  data () {
    return {
      autoSaveTimer: null,
      booting: true
    }
  },
  async mounted () {
    document.body.classList.add('journey-builder-page')

    try {
      const id = this.$route.params.id
      if (id && id !== 'new') {
        // Jornada e dados de referência em paralelo — o overlay só sai
        // quando os dois terminam
        await Promise.all([this.getJourney(id), this.loadReferenceData()])
      } else {
        // Modo novo: jornada local sem ID
        this.$store.commit('journeyBuilder/JOURNEY/SET_JOURNEY', {
          id: null,
          name: '',
          description: '',
          status: 'draft',
          nodes: []
        })
        await this.loadReferenceData()
      }
    } catch (err) {
      errorMessage('Falha ao carregar a jornada. Recarregue a página.')
      console.error(err)
    } finally {
      this.booting = false
    }

    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy () {
    document.body.classList.remove('journey-builder-page')
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
    if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
  },
  methods: {
    ...mapActions('journeyBuilder', {
      getJourney: 'GetJourney',
      saveAction: 'SaveJourney',
      changeStatusAction: 'ChangeStatus',
      addNodeAction: 'AddNode',
      updateNodeAction: 'UpdateNode',
      removeNodeAction: 'RemoveNode',
      reorderNodesAction: 'ReorderNodes',
      selectNodeAction: 'SelectNode',
      updateJourneyFieldAction: 'UpdateJourneyField',
      loadReferenceData: 'LoadReferenceData'
    }),
    goBack () {
      if (this.dirty) {
        if (!confirm('Existem alteracoes nao salvas. Deseja sair?')) {
          return
        }
      }
      this.$router.push('/journeys')
    },
    async saveJourney () {
      if (!this.journey || !this.journey.name || !this.journey.name.trim()) {
        errorMessage('Insira o nome da jornada antes de salvar.')
        this.$nextTick(() => {
          const input = this.$el.querySelector('.journey-title-input')
          if (input) input.focus()
        })
        return
      }
      try {
        const isNew = !this.journey.id
        await this.saveAction()
        successMessage('Jornada salva com sucesso.', 2000)
        if (isNew && this.journey.id) {
          this.$router.replace(`/journeys/${this.journey.id}`)
        }
      } catch (err) {
        errorMessage('Erro ao salvar: ' + (err.message || err))
      }
    },
    async changeJourneyStatus (status) {
      if (!this.journey || !this.journey.name || !this.journey.name.trim()) {
        errorMessage('Insira o nome da jornada antes de alterar o status.')
        return
      }
      try {
        if (this.dirty || !this.journey.id) {
          const isNew = !this.journey.id
          await this.saveAction()
          if (isNew && this.journey.id) {
            this.$router.replace(`/journeys/${this.journey.id}`)
          }
        }
        await this.changeStatusAction({ id: this.journey.id, status })
        successMessage('Status atualizado com sucesso.', 2000)
      } catch (err) {
        errorMessage('Erro ao alterar status: ' + (err.message || err))
      }
    },
    addNode (nodeDef) {
      const node = {
        ...nodeDef,
        _tempId: 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        config: nodeDef.config ? { ...nodeDef.config } : {}
      }
      this.addNodeAction(node)
    },
    addNodeAt ({ node, index }) {
      const newNode = {
        ...node,
        _tempId: 'temp_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        config: node.config ? { ...node.config } : {}
      }
      if (newNode.parent_node_id) {
        // Branch node - just push to nodes array
        this.addNodeAction(newNode)
      } else {
        // Main flow node - splice at index
        const nodes = [...this.nodes]
        nodes.splice(index, 0, newNode)
        this.reorderNodesAction(nodes)
      }
    },
    updateNode (node) {
      this.updateNodeAction(node)
      this.scheduleAutoSave()
    },
    scheduleAutoSave () {
      if (this.autoSaveTimer) clearTimeout(this.autoSaveTimer)
      this.autoSaveTimer = setTimeout(() => { this.autoSave() }, 1500)
    },
    async autoSave () {
      // Save em andamento: reagenda em vez de desistir. Antes esta chamada dava
      // `return` seco e as alterações daquela janela nunca subiam pro servidor.
      if (this.saving) {
        this.scheduleAutoSave()
        return
      }
      if (!this.dirty) return
      if (!this.journey || !this.journey.name || !this.journey.name.trim()) return
      try {
        const isNew = !this.journey.id
        await this.saveAction()
        if (isNew && this.journey.id) {
          this.$router.replace(`/journeys/${this.journey.id}`)
        }
        // O que foi editado durante o request continua pendente — nova rodada.
        if (this.dirty) this.scheduleAutoSave()
      } catch (err) {
        console.error('Auto-save failed:', err)
      }
    },
    removeNode (nodeId) {
      this.removeNodeAction(nodeId)
    },
    selectNode (nodeId) {
      this.selectNodeAction(nodeId)
    },
    reorderNodes (nodes) {
      this.reorderNodesAction(nodes)
    },
    markDirty () {
      this.$store.commit('journeyBuilder/JOURNEY/SET_DIRTY', true)
    },
    statusLabel (status) {
      const labels = { draft: 'Rascunho', active: 'Ativa', paused: 'Pausada', archived: 'Arquivada' }
      return labels[status] || status
    },
    handleBeforeUnload (e) {
      if (this.dirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
  },
  computed: {
    ...mapState('journeyBuilder', [
      'journey', 'nodes', 'selectedNodeId', 'dirty', 'saving',
      'emailTemplates', 'waTemplates', 'statuses', 'groups', 'tags', 'forms', 'journeysList', 'fieldTypes', 'refStatuses', 'formConditionFields', 'followUpRules'
    ]),
    ...mapGetters('journeyBuilder', {
      selectedNode: 'SELECTED_NODE',
      mainFlowNodes: 'MAIN_FLOW_NODES'
    })
  }
}
</script>

<style scoped>
.journey-builder {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
  overflow: hidden;
  position: relative;
}

/* Loading Overlay (padrão form-builder) */
.builder-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(245, 246, 250, 0.82);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
}
.builder-loading-content {
  text-align: center;
  background: #fff;
  padding: 32px 48px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}
.builder-loading-content p {
  margin: 16px 0 0;
  font-size: 15px;
  font-weight: 500;
  color: #495057;
}
.builder-loading-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: 3px solid #e9ecef;
  border-top-color: #556ee6;
  border-radius: 50%;
  animation: builder-spin 0.8s linear infinite;
}
@keyframes builder-spin {
  to { transform: rotate(360deg); }
}
.fade-overlay-enter-active {
  transition: opacity 0.15s ease;
}
.fade-overlay-leave-active {
  transition: opacity 0.3s ease;
}
.fade-overlay-enter,
.fade-overlay-leave-to {
  opacity: 0;
}

/* Header - matches form-builder */
.builder-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.btn-back {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  color: #495057;
  transition: all 0.15s;
}
.btn-back:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.journey-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.journey-title-input {
  border: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
  padding: 4px 0;
  width: 300px;
}
.journey-title-input:focus {
  outline: none;
  border-bottom: 2px solid #556ee6;
}

.journey-status {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}
.journey-status.draft { background: #e9ecef; color: #495057; }
.journey-status.active { background: #d1fae5; color: #059669; }
.journey-status.paused { background: #fef3c7; color: #d97706; }
.journey-status.archived { background: #e5e7eb; color: #6b7280; }

.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-status-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid #e9ecef;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-status-toggle:hover {
  border-color: #556ee6;
  color: #556ee6;
}

.btn-save {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 20px;
  border: none;
  background: #556ee6;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}
.btn-save:hover:not(:disabled) { background: #485ec4; }
.btn-save:disabled { opacity: 0.7; cursor: not-allowed; }

/* Main Content */
.builder-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-left {
  width: 280px;
  border-right: 1px solid #e9ecef;
  background: #fff;
  flex-shrink: 0;
}

.panel-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #f5f6fa;
}

.canvas-scroll {
  flex: 1;
  overflow-y: auto;
  overflow-x: auto;
  padding: 20px;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.canvas-scroll:hover {
  scrollbar-color: #d0d5dd transparent;
}
.canvas-scroll::-webkit-scrollbar { width: 5px; }
.canvas-scroll::-webkit-scrollbar-track { background: transparent; }
.canvas-scroll::-webkit-scrollbar-thumb { background-color: transparent; border-radius: 3px; }
.canvas-scroll:hover::-webkit-scrollbar-thumb { background-color: #d0d5dd; }
.canvas-scroll::-webkit-scrollbar-thumb:hover { background-color: #b0b5bd; }

.panel-right {
  width: 0;
  background: #fff;
  border-left: 1px solid #e9ecef;
  transition: width 0.2s ease;
  overflow: hidden;
  flex-shrink: 0;
}
.panel-right.has-selection { width: 360px; }

/* No selection state */
.no-selection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
}
.no-selection-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f4ff;
  border-radius: 50%;
  margin-bottom: 16px;
}
.no-selection-icon i { font-size: 28px; color: #556ee6; }
.no-selection h5 { font-size: 15px; font-weight: 600; color: #495057; margin-bottom: 8px; }
.no-selection p { font-size: 13px; color: #6c757d; margin: 0; }
</style>

<style>
/* Global style for full-bleed layout - matches form-builder pattern */
body.journey-builder-page {
  overflow: hidden !important;
  height: 100vh !important;
}

body.journey-builder-page footer.footer {
  display: none !important;
}

body.journey-builder-page .main-content {
  padding: 0 !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  margin-right: 0 !important;
  overflow: hidden !important;
  height: 100vh !important;
  display: flex !important;
  flex-direction: column !important;
}

body.journey-builder-page .page-content {
  padding: 0 !important;
  margin: 0 !important;
  margin-top: 70px !important;
  overflow: hidden !important;
  min-height: 0 !important;
  height: calc(100vh - 70px) !important;
  max-height: calc(100vh - 70px) !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
}

body.journey-builder-page .page-content > .container-fluid {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  min-height: 0 !important;
  max-width: 100% !important;
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
</style>
